import { useMemo } from "react";
import { useData } from "@/lib/store";
import { useSearchParams } from "react-router-dom";
import { TYPE_META, cn } from "@/lib/utils";

const DAYS = 14;

export default function TimelinePage() {
  const { items, spaces } = useData();
  const [params] = useSearchParams();
  const spaceFilter = params.get("space");

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Array.from({ length: DAYS }, (_, i) => {
    const d = new Date(today); d.setDate(today.getDate() + i); return d;
  });

  const rows = useMemo(() => {
    let r = items.filter((i) => i.due_date && i.status !== "done" && !i.archived);
    if (spaceFilter) r = r.filter((i) => i.space_id === spaceFilter);
    return r.sort((a, b) => a.due_date.localeCompare(b.due_date));
  }, [items, spaceFilter]);

  const colFor = (iso) => {
    const dt = new Date(iso + "T00:00:00"); dt.setHours(0, 0, 0, 0);
    const diff = Math.round((dt - today) / 86400000);
    return diff;
  };

  return (
    <div className="animate-fade-in-up">
      <div className="mb-4 flex items-center justify-end">
        <p className="text-sm text-surface-onVariant">Next 14 days</p>
      </div>

      <div className="rounded-hero bg-surface-container p-6 overflow-x-auto scrollbar-thin" data-testid="timeline-canvas">
        <div className="min-w-[900px]">
          {/* Header row */}
          <div className="grid mb-4" style={{ gridTemplateColumns: `220px repeat(${DAYS}, minmax(60px, 1fr))` }}>
            <div />
            {days.map((d, i) => (
              <div key={i} className={cn(
                "text-center px-1",
                i === 0 && "text-primary font-medium"
              )}>
                <div className="text-[10px] uppercase tracking-widest text-surface-onVariant">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                <div className="text-sm text-surface-on">{d.getDate()}</div>
              </div>
            ))}
          </div>

          {rows.length === 0 && <div className="text-center py-16 text-sm text-surface-onVariant">Nothing scheduled in the next two weeks.</div>}

          {rows.map((it) => {
            const col = colFor(it.due_date);
            const clampedCol = Math.max(0, Math.min(DAYS - 1, col));
            const overdue = col < 0;
            const space = spaces.find((s) => s.id === it.space_id);
            const type = TYPE_META[it.type];
            return (
              <div key={it.id} data-testid={`timeline-row-${it.id}`} className="grid items-center py-2 border-t border-outline-variant" style={{ gridTemplateColumns: `220px repeat(${DAYS}, minmax(60px, 1fr))` }}>
                <div className="pr-3 min-w-0">
                  <div className="text-sm font-medium text-surface-on truncate">{it.title}</div>
                  <div className="text-[10px] text-surface-onVariant flex items-center gap-1.5 mt-0.5">
                    {space && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: space.color }} />}
                    <span>{space?.name}</span>
                  </div>
                </div>
                {days.map((_, i) => {
                  const active = i === clampedCol;
                  return (
                    <div key={i} className="h-8 px-1 flex items-center">
                      {active && (
                        <div
                          className={cn(
                            "h-6 rounded-full px-3 text-[10px] font-medium tracking-wide uppercase flex items-center whitespace-nowrap",
                            overdue ? "bg-danger-container text-danger" : type.color
                          )}
                        >
                          {overdue ? "Overdue" : type.label}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
